import type { Locale, ServicePage } from "./types"

/**
 * Service definitions.
 *
 * Every service owns an independent crawlable URL in both locales.
 * Copy must stay factual: only reference work and capabilities supported by
 * existing approved site content (De Limburger, Burgerkracht, the trading
 * platform project and the founders' Maastricht University background).
 */
export const services: ServicePage[] = [
  {
    id: "ai-development",
    pageType: "service",
    slug: { nl: "ai-development", en: "ai-development" },
    title: {
      nl: "Software op Maat & AI Development Limburg",
      en: "Custom AI Development & Engineering",
    },
    description: {
      nl: "Software op maat en AI-development vanuit Limburg door ingenieurs van de Universiteit Maastricht. Van prototype tot productieklare toepassing, volledig geïntegreerd met uw systemen.",
      en: "Custom AI development by engineers trained at Maastricht University. From prototype to production-ready AI applications, fully integrated with your systems.",
    },
    h1: {
      nl: "Software op Maat & AI Development",
      en: "Custom AI Development",
    },
    shortDescription: {
      nl: "Van concept tot productie: we ontwerpen en bouwen schaalbare, productieklare AI-applicaties die echte problemen oplossen.",
      en: "From concept to deployment: we design and build scalable, production-ready AI applications that solve real problems.",
    },
    intro: {
      heading: {
        nl: "AI-systemen die werken in de praktijk, niet alleen in een demo",
        en: "AI systems that work in practice, not just in demos",
      },
      body: {
        nl: "Maatwerk AI-development betekent dat het systeem rond jouw organisatie wordt gebouwd — niet andersom. Als ingenieurs met een achtergrond in Data Science & AI aan de Universiteit Maastricht bouwen wij volledige AI-oplossingen: van datamodel en taalmodel tot API, interface en hosting. Geen losse experimenten, maar systemen die dagelijks draaien en beheersbaar zijn. Zo ontwikkelden wij eerder een geautomatiseerd contentdistributiesysteem voor een toonaangevend regionale uitgeverij en een marktintelligentiesysteem voor een institutionele trading-partner.",
        en: "Custom AI development means the system is built around your organisation — not the other way around. As engineers with a Data Science & AI background from Maastricht University, we build complete AI solutions: from data and language models to APIs, interfaces and hosting. No throwaway experiments, but systems that run daily and remain maintainable. Previous work includes an automated content-distribution system for a leading regional publisher and a market-intelligence system for an institutional trading partner.",
      },
    },
    capabilities: [
      {
        title: { nl: "Ontwikkeling en fine-tuning van AI-modellen", en: "AI model development and fine-tuning" },
        description: {
          nl: "We selecteren, combineren en stemmen modellen af op jouw data en taak — van grote taalmodellen tot gespecialiseerde classificatie- of voorspelmodellen.",
          en: "We select, combine and tune models around your data and task — from large language models to specialised classification or forecasting models.",
        },
      },
      {
        title: { nl: "Full-stack applicatie-engineering", en: "Full-stack application engineering" },
        description: {
          nl: "Een AI-model is pas waardevol wanneer gebruikers ermee kunnen werken. Wij bouwen de applicaties eromheen: interfaces, workflows en beheer.",
          en: "A model only creates value when people can actually use it. We build everything around it: interfaces, workflows and tooling.",
        },
      },
      {
        title: { nl: "API-ontwerp en naadloze integratie", en: "API design and seamless integration" },
        description: {
          nl: "We koppelen AI-systemen aan bestaande bedrijfssoftware, databronnen en platforms, zodat resultaten daar landen waar je team werkt.",
          en: "We connect AI systems to your existing business software, data sources and platforms, so results land where your team already works.",
        },
      },
      {
        title: { nl: "Performance-optimalisatie en schaling", en: "Performance optimisation and scaling" },
        description: {
          nl: "Snelheid, kosten per request en betrouwbaarheid worden vanaf dag één meegenomen in het ontwerp, niet achteraf erbij geprogrammeerd.",
          en: "Latency, cost per request and reliability are part of the design from day one — not retrofitted afterwards.",
        },
      },
      {
        title: { nl: "Continue deployment en onderhoud", en: "Continuous deployment and maintenance" },
        description: {
          nl: "Modellen en hun omgeving veranderen snel. We leveren systemen die bijgewerkt, gemonitord en doorontwikkeld kunnen worden.",
          en: "Models and their environment change fast. We deliver systems that can be updated, monitored and evolved over time.",
        },
      },
    ] ,
    suitableFor: [
      {
        nl: "Je hebt een proces of product waarin taal-, data- of beslishandelingen veel handmatige tijd kosten.",
        en: "You have a process or product where language, data or decision-making consumes significant manual time.",
      },
      {
        nl: "Bestaande standaardsoftware dekt je situatie niet, omdat jouw data of workflow specifiek is.",
        en: "Off-the-shelf software does not cover your situation because your data or workflow is specific.",
      },
      {
        nl: "Je wilt een systeem dat je blijft ontwikkelen en bezitten, met korte lijnen naar de bouwers.",
        en: "You want a system you keep owning and evolving, with a direct line to the people who build it.",
      },
    ] ,
    process: [
      {
        title: { nl: "Verkenning & haalbaarheid", en: "Exploration & feasibility" },
        description: {
          nl: "We brengen het probleem, de beschikbare data en de randvoorwaarden in kaart en geven eerlijk aan of AI hier de juiste oplossing is.",
          en: "We map the problem, available data and constraints, and honestly tell you whether AI is the right solution here.",
        },
      },
      {
        title: { nl: "Prototype & validatie", en: "Prototype & validation" },
        description: {
          nl: "In korte iteraties bouwen we een werkend prototype waarmee we aannemen testen tegen de praktijk van jouw team.",
          en: "In short iterations we build a working prototype to test assumptions against the reality of your team.",
        },
      },
      {
        title: { nl: "Productie-bouw & integratie", en: "Production build & integration" },
        description: {
          nl: "Het prototype groeit uit tot een productieklare applicatie met integraties, monitoring en documentatie.",
          en: "The prototype grows into a production-ready application with integrations, monitoring and documentation.",
        },
      },
      {
        title: { nl: "Beheer & doorontwikkeling", en: "Operations & evolution" },
        description: {
          nl: "Na livegang blijven we monitoren, verbeteren en uitbreiden zodat het systeem mee beweegt met je organisatie.",
          en: "After go-live we keep monitoring, improving and extending so the system moves with your organisation.",
        },
      },
    ] ,
    relatedCaseIds: ["de-limburger", "quantitative-trading"],
    relatedKnowledgeIds: ["van-ai-poc-naar-productie", "ai-integreren-bedrijfssoftware", "wat-kost-maatwerk-ai"],
  },

  {
    id: "ai-automation",
    pageType: "service",
    slug: { nl: "ai-automatisering", en: "ai-automation" },
    title: {
      nl: "Bedrijfsprocessen Automatiseren met AI | AI Automatisering",
      en: "AI Automation for Business Processes",
    },
    description: {
      nl: "Bedrijfsprocessen automatiseren met AI: handmatige processen worden geautomatiseerd. Repetitief werk zoals categoriseren, plaatsen, classificeren en verwerken wordt intelligent en automatisch uitgevoerd.",
      en: "Automate business processes with AI: repetitive work such as categorising, placing, classifying and processing gets executed intelligently and automatically.",
    },
    h1: {
      nl: "Bedrijfsprocessen Automatiseren met AI",
      en: "AI Automation",
    },
    shortDescription: {
      nl: "We vervangen repetitieve handmatige processen door intelligente workflows die zelf beslissingen nemen binnen jouw regels.",
      en: "We replace repetitive manual processes with intelligent workflows that make decisions themselves within your rules.",
    },
    intro: {
      heading: {
        nl: "Repetitief digitaal werk weg, focus terug naar waardevolle taken",
        en: "Remove repetitive digital work, get focus back on valuable tasks",
      },
      body: {
        nl: "AI-automatisering gaat verder dan klassieke scripts: het systeem begrijpt inhoud, trekt conclusies en handelt daarna. Voor een toonaangevende regionale uitgeverij vervingen wij het dagelijkse handmatige plaatsen van content over tientallen doelgroepen door een intelligent systeem dat automatisch herkent welke content relevant is en deze op de juiste locaties plaatst — met aanzienlijke dagelijkse tijdsbesparing als resultaat. Dat is het patroon dat we ook voor andere processen toepassen: inhoud begrijpen, regels toepassen, actie ondernemen.",
        en: "AI automation goes beyond classic scripting: the system understands content, draws conclusions, and then acts. For a leading regional publisher, we replaced the daily manual placement of content across dozens of audiences with an intelligent system that automatically recognises relevant content and places it in the right locations — delivering significant daily time savings. That is the pattern we apply to other processes too: understand the content, apply the rules, take action.",
      },
    },
    capabilities: [
      {
        title: { nl: "Contentclassificatie en -routering", en: "Content classification and routing" },
        description: {
          nl: "Documenten, berichten of items automatisch lezen, categoriseren en naar het juiste kanaal of de juiste persoon sturen.",
          en: "Automatically read, categorise and route documents, messages or items to the right channel or person.",
        },
      },
      {
        title: { nl: "Geautomatiseerde publicatie-workflows", en: "Automated publication workflows" },
        description: {
          nl: "Volledige distributieketens automatiseren — van invoer tot plaatsing op platforms, inclusief validatie en foutafhandeling.",
          en: "Automate full distribution chains — from input to placement on platforms, including validation and error handling.",
        },
      },
      {
        title: { nl: "Dataverwerking en -extractie", en: "Data processing and extraction" },
        description: {
          nl: "Ongestructureerde informatie (documenten, e-mails, rapportages) omzetten naar gestructureerde, bruikbare data.",
          en: "Turn unstructured information (documents, emails, reports) into structured, usable data.",
        },
      },
      {
        title: { nl: "Bewaking en menselijke controle", en: "Guardrails and human review" },
        description: {
          nl: "Automatisering met controles: drempels, samenvattingen en goedkeurstappen waar dat nodig is, zodat het proces vertrouwd kan worden.",
          en: "Automation with controls: thresholds, summaries and approval steps where needed, so the process can be trusted.",
        },
      },
    ] ,
    suitableFor: [
      {
        nl: "Je team voert dagelijks dezelfde digitale handelingen uit: plaatsen, labelen, kopiëren, controleren, doorsturen.",
        en: "Your team performs the same digital actions daily: placing, labelling, copying, checking, forwarding.",
      },
      {
        nl: "Het proces heeft duidelijke regels, maar de variatie in inhoud maakt klassieke automatisering te star.",
        en: "The process has clear rules, but variation in content makes classic automation too rigid.",
      },
      {
        nl: "De hoeveelheid werk groeit mee met het aanbod, waardoor handmatig werken niet meer opschaalt.",
        en: "The workload scales with supply, so manual handling no longer keeps up.",
      },
    ] ,
    process: [
      {
        title: { nl: "Procesanalyse", en: "Process analysis" },
        description: {
          nl: "We lopen het huidige proces stap voor stap door en identificeren welke handelingen betrouwbaar automatiseerbaar zijn.",
          en: "We walk through the current process step by step and identify which actions can reliably be automated.",
        },
      },
      {
        title: { nl: "Platform- en randvoorwaarden verkennen", en: "Platform and constraint exploration" },
        description: {
          nl: "Zoals bij De Limburger testen we eerst de grenzen van de platforms en systemen waar het proces mee werkt.",
          en: "As with De Limburger, we first probe the limits of the platforms and systems involved in the process.",
        },
      },
      {
        title: { nl: "Workflow bouwen", en: "Workflow build" },
        description: {
          nl: "We bouwen de geautomatiseerde workflow met categorisatie- en beslislogica, gekoppeld aan jouw systemen.",
          en: "We build the automated workflow with categorisation and decision logic, connected to your systems.",
        },
      },
      {
        title: { nl: "Monitoren en bijsturen", en: "Monitor and refine" },
        description: {
          nl: "Livegang is het begin: we bewaken kwaliteit en stellen regels bij waar het proces om vraagt.",
          en: "Going live is the start: we monitor quality and adjust rules wherever the process requires it.",
        },
      },
    ] ,
    relatedCaseIds: ["de-limburger"],
    relatedKnowledgeIds: ["ai-agents-vs-automatisering", "ai-integreren-bedrijfssoftware"],
  },

  {
    id: "ai-agents",
    pageType: "service",
    slug: { nl: "ai-agents", en: "ai-agents" },
    title: {
      nl: "AI Agent Laten Maken of Bouwen | Maatwerk AI Agents",
      en: "Build a Custom AI Agent | AI Agents for Business",
    },
    description: {
      nl: "Een AI-agent laten maken of bouwen? Wij bouwen AI-agents die meer doen dan praten: tools aansturen, bedrijfsdata raadplegen en taken uitvoeren onder menselijke controle.",
      en: "Have an AI agent built that does more than talk: operating tools, consulting company data and executing tasks under human oversight.",
    },
    h1: {
      nl: "AI Agent Laten Maken of Bouwen",
      en: "AI Agents for your organisation",
    },
    shortDescription: {
      nl: "AI-agents die zelfstandig taken uitvoeren: zoeken in bedrijfsdata, systemen aansturen en acties ondernemen — met duidelijke grenzen en controles.",
      en: "AI agents that execute tasks autonomously: searching company data, operating systems and taking action — with clear boundaries and controls.",
    },
    intro: {
      heading: {
        nl: "Een agent handelt; een chatbot antwoordt alleen",
        en: "An agent acts; a chatbot merely answers",
      },
      body: {
        nl: "Het verschil tussen een gewone chatbot en een AI-agent is actie. Een agent kan tools en API's aanroepen, bedrijfsdata raadplegen en meerstaps-taken uitvoeren — bijvoorbeeld een gesprek voeren, de situatie van de gebruiker begrijpen en vervolgens automatisch de juiste vervolgactie kiezen. Precies zo bouwden wij de navigatieassistent voor Burgerkracht: een conversationele agent die via een kennisbank-gebaseerde retrievalarchitectuur dynamisch relevante informatie ophaalt en bezoekers automatisch doorverwijst naar de meest relevante groepen of pagina's.",
        en: "The difference between an ordinary chatbot and an AI agent is action. An agent can call tools and APIs, consult company data and execute multi-step tasks — for instance holding a conversation, understanding the user's situation and then automatically choosing the right follow-up action. That is exactly how we built the navigation assistant for Burgerkracht: a conversational agent that uses knowledge-base-driven retrieval to dynamically fetch relevant information and automatically refer visitors to the most relevant groups or pages.",
      },
    },
    capabilities: [
      {
        title: { nl: "Gesprekken met begrip van context", en: "Conversations that understand context" },
        description: {
          nl: "Natuurlijke dialogen waarbij de agent de intentie en situatie van de gebruiker herkent in plaats van trefwoorden te matchen.",
          en: "Natural dialogue in which the agent recognises user intent and situation instead of matching keywords.",
        },
      },
      {
        title: { nl: "Koppeling met tools en API's", en: "Tool and API integration" },
        description: {
          nl: "Agents kunnen systemen aansturen: data ophalen, records bijwerken, taken aanmaken of processen starten.",
          en: "Agents can operate systems: retrieve data, update records, create tasks or trigger processes.",
        },
      },
      {
        title: { nl: "Toegang tot bedrijfsdata via retrieval", en: "Access to company data via retrieval" },
        description: {
          nl: "Met een kennisbank-architectuur (RAG) antwoordt de agent op basis van jouw eigen documenten en informatie, niet op algemene kennis.",
          en: "With a knowledge-base architecture (RAG), the agent answers based on your own documents and information, not general knowledge.",
        },
      },
      {
        title: { nl: "Grenzen, goedkeuring en logging", en: "Boundaries, approvals and logging" },
        description: {
          nl: "We definiëren per agent wat deze zelfstandig mag doen en waar een mens moet goedkeuren — met logging van elke actie.",
          en: "We define per agent what it may do autonomously and where a human must approve — logging every action.",
        },
      },
      {
        title: { nl: "Productie-aandachtspunten", en: "Production concerns handled" },
        description: {
          nl: "Kwaliteitsbewaking, kostenbeheersing, misbruikpreventie en updates van onderliggende modellen horen bij onze aanpak.",
          en: "Quality monitoring, cost control, abuse prevention and model updates are part of our approach.",
        },
      },
    ] ,
    suitableFor: [
      {
        nl: "Bezoekers of medewerkers stellen steeds dezelfde vragen, maar het juiste antwoord hangt af van hun situatie.",
        en: "Visitors or employees ask similar questions repeatedly, yet the right answer depends on their situation.",
      },
      {
        nl: "Een taak vereist meerdere stappen over verschillende systemen heen.",
        en: "A task requires multiple steps across different systems.",
      },
      {
        nl: "Je wilt beschikbaarheid buiten kantooruren zonder kwaliteit te verliezen.",
        en: "You want availability outside office hours without losing quality.",
      },
    ] ,
    process: [
      {
        title: { nl: "Taken en machtiging bepalen", en: "Define tasks and authority" },
        description: {
          nl: "Samen stellen we vast welke taken de agent uitvoert, welke bronnen hij raadpleegt en welke acties menselijke goedkeuring vereisen.",
          en: "Together we determine which tasks the agent executes, which sources it consults and which actions need human approval.",
        },
      },
      {
        title: { nl: "Kennisbank en tools koppelen", en: "Connect knowledge base and tools" },
        description: {
          nl: "We ontsluiten documentatie, data en systemen zodat de agent feitelijke, actuele informatie gebruikt.",
          en: "We expose documentation, data and systems so the agent uses factual, current information.",
        },
      },
      {
        title: { nl: "Agent bouwen en testen", en: "Build and test the agent" },
        description: {
          nl: "We bouwen de agent en testen gesprekken en acties tegen realistische scenario's uit jouw praktijk.",
          en: "We build the agent and test conversations and actions against realistic scenarios from your practice.",
        },
      },
      {
        title: { nl: "Uitrollen en bewaken", en: "Deploy and monitor" },
        description: {
          nl: "Na livegang monitoren we kwaliteit, kosten en gebruik en stellen we gedrag en kennis bij.",
          en: "After launch we monitor quality, cost and usage, refining behaviour and knowledge as needed.",
        },
      },
    ] ,
    faq: [
      {
        question: { nl: "Wat is een AI-agent?", en: "What is an AI agent?" },
        answer: {
          nl: "Een AI-agent is een systeem dat niet alleen antwoordt, maar ook acties uitvoert: tools en API's aanroepen, bedrijfsdata raadplegen en meerstaps-taken afhandelen, binnen grenzen die jij bepaalt.",
          en: "An AI agent is a system that does not just answer but also takes action: calling tools and APIs, consulting company data and handling multi-step tasks, within boundaries you define.",
        },
      },
      {
        question: {
          nl: "Kan ik een AI-agent laten maken die met mijn eigen bedrijfsdata werkt?",
          en: "Can I have an AI agent built that works with my own company data?",
        },
        answer: {
          nl: "Ja. Met een retrieval-architectuur (RAG) baseert de agent zijn antwoorden op jouw eigen documenten en systemen in plaats van op algemene modelkennis.",
          en: "Yes. With a retrieval architecture (RAG), the agent bases its answers on your own documents and systems rather than on general model knowledge.",
        },
      },
      {
        question: { nl: "Houdt een mens de controle?", en: "Does a human stay in control?" },
        answer: {
          nl: "Ja. Per agent bepalen we wat deze zelfstandig mag doen en waar een mens moet goedkeuren. Elke actie wordt gelogd.",
          en: "Yes. For each agent we define what it may do autonomously and where a human must approve. Every action is logged.",
        },
      },
      {
        question: { nl: "Wat kost het om een AI-agent te laten maken?", en: "What does it cost to have an AI agent built?" },
        answer: {
          nl: "Dat hangt af van het aantal systemen dat de agent moet aansturen, de staat van je data en de kwaliteitseisen. Daarom werken we in fasen, beginnend met een verkenning van haalbaarheid en waarde.",
          en: "It depends on the number of systems the agent must operate, the state of your data and the quality requirements. That is why we work in phases, starting with an exploration of feasibility and value.",
        },
      },
    ],
    relatedCaseIds: ["burgerkracht"],
    relatedKnowledgeIds: ["ai-agent-laten-bouwen", "wat-is-een-ai-agent", "ai-agent-bedrijfsdata", "ai-agents-vs-automatisering"],
  },

  {
    id: "ai-workshops",
    pageType: "service",
    slug: { nl: "ai-workshops", en: "ai-workshops" },
    title: {
      nl: "AI Workshop voor Bedrijven | Praktisch & op Maat",
      en: "AI Workshops for Businesses",
    },
    description: {
      nl: "AI-workshop voor bedrijven: praktische sessies voor teams, van AI-geletterdheid en hands-on sessies tot strategische implementatieplanning en executive briefings.",
      en: "Hands-on AI workshops for teams: from AI literacy and practical coding sessions to strategic implementation planning and executive briefings.",
    },
    h1: {
      nl: "AI Workshop voor Bedrijven",
      en: "AI Workshops & Presentations",
    },
    shortDescription: {
      nl: "Trainingen en briefings die de kloof overbruggen tussen AI-theorie en praktische toepassing in jouw organisatie.",
      en: "Training and briefings that bridge the gap between AI theory and practical application in your organisation.",
    },
    intro: {
      heading: {
        nl: "Van AI-theorie naar toepassing in jouw eigen praktijk",
        en: "From AI theory to application in your own practice",
      },
      body: {
        nl: "De snelste manier om waarde uit AI te halen, is een team dat begrijpt wat AI wél en níét kan. In onze workshops vertalen we complexe AI-concepten naar heldere praktijk: interactieve sessies met echte voorbeelden, hands-on codeerervaring en strategische planning die past bij jouw sector. Naast workshops verzorgen we ook presentaties en executive briefings voor leidinggevenden en technische teams, zodat beslissingen over AI op feiten staan in plaats van op hype.",
        en: "The fastest way to extract value from AI is a team that understands what AI can and cannot do. In our workshops we translate complex AI concepts into clear practice: interactive sessions with real examples, hands-on coding experience and strategic planning tailored to your industry. Alongside workshops we deliver presentations and executive briefings for leadership and technical teams, so decisions about AI rest on facts rather than hype.",
      },
    },
    capabilities: [
      {
        title: { nl: "Team AI-geletterdheid en capaciteitsopbouw", en: "Team AI literacy and capability building" },
        description: {
          nl: "We brengen teams op het niveau waar ze zelf kansen en risico's van AI herkennen in hun dagelijkse werk.",
          en: "We bring teams to the level where they recognise opportunities and risks of AI in their daily work themselves.",
        },
      },
      {
        title: { nl: "Praktische sessies met echte voorbeelden", en: "Practical sessions with real examples" },
        description: {
          nl: "Hands-on codeersessies en demo's met materialen uit echte projecten in plaats van losse slideshows.",
          en: "Hands-on coding sessions and demos using materials from real projects rather than isolated slide decks.",
        },
      },
      {
        title: { nl: "Strategische AI-implementatieplanning", en: "Strategic AI implementation planning" },
        description: {
          nl: "Samenwerken aan een concreet plan: welke processen eerst, welke techniek passend, welke investering verantwoord.",
          en: "Working towards a concrete plan: which processes first, which technology fits, which investment is justified.",
        },
      },
      {
        title: { nl: "Toolselectie en evaluatiebegeleiding", en: "Tool selection and evaluation guidance" },
        description: {
          nl: "We helpen het bos door de bomen te zien: welke tools en modellen passen bij jouw doelen en budget.",
          en: "We help you see the wood for the trees: which tools and models fit your goals and budget.",
        },
      },
      {
        title: { nl: "Executive briefings en deep-dives", en: "Executive briefings and technical deep-dives" },
        description: {
          nl: "Van bestuurskamer tot developmentteam: heldere verhalen over AI-kansen, trends en technische architectuur.",
          en: "From boardroom to dev team: clear narratives about AI opportunities, trends and technical architecture.",
        },
      },
    ] ,
    suitableFor: [
      {
        nl: "Je organisatie wil met AI aan de slag, maar weet nog niet waar of hoe.",
        en: "Your organisation wants to get started with AI but doesn't know where or how yet.",
      },
      {
        nl: "Je team werkt al met AI-tools en wil sneller en veiliger worden in het toepassen ervan.",
        en: "Your team already works with AI tools and wants to become faster and safer at applying them.",
      },
      {
        nl: "Leidinggevenden willen AI-beslissingen nemen op basis van inzicht in plaats van marketing.",
        en: "Leadership wants to make AI decisions based on insight rather than marketing.",
      },
    ] ,
    process: [
      {
        title: { nl: "Doelgroep en niveau bepalen", en: "Determine audience and level" },
        description: {
          nl: "Elke sessie beginnen we met de vraag: wie neemt deel, wat weten ze al en wat moeten ze daarna kunnen?",
          en: "Every session starts with the question: who participates, what do they know, and what must they be able to do afterwards?",
        },
      },
      {
        title: { nl: "Curriculum op maat", en: "Tailored curriculum" },
        description: {
          nl: "We stellen een programma samen met voorbeelden en cases die aansluiten bij jouw sector en systemen.",
          en: "We compose a programme with examples and cases that match your industry and systems.",
        },
      },
      {
        title: { nl: "Interactieve sessie", en: "Interactive session" },
        description: {
          nl: "Workshop of briefing met ruimte voor vragen, demo's en oefening — geen eenrichtingsverkeer.",
          en: "Workshop or briefing with room for questions, demos and exercises — never one-directional.",
        },
      },
      {
        title: { nl: "Vervolgstappen", en: "Next steps" },
        description: {
          nl: "Na afloop ontvang je concrete vervolgstappen en eventueel een plan voor opvolgende sessies of projecten.",
          en: "Afterwards you receive concrete next steps and optionally a plan for follow-up sessions or projects.",
        },
      },
    ] ,
    faq: [
      {
        question: { nl: "Voor wie is een AI-workshop geschikt?", en: "Who is an AI workshop for?" },
        answer: {
          nl: "Voor teams die met AI willen beginnen, teams die al met AI-tools werken en sneller en veiliger willen worden, en leidinggevenden die AI-beslissingen op inzicht willen baseren.",
          en: "For teams that want to get started with AI, teams that already use AI tools and want to become faster and safer, and leaders who want to base AI decisions on insight.",
        },
      },
      {
        question: { nl: "Is de workshop op maat?", en: "Is the workshop tailored?" },
        answer: {
          nl: "Ja. We bepalen eerst wie deelneemt en wat ze al weten, en stellen daarna een programma samen met voorbeelden die passen bij jouw sector en systemen.",
          en: "Yes. We first determine who participates and what they already know, then compose a programme with examples that fit your industry and systems.",
        },
      },
      {
        question: { nl: "Waar vindt de workshop plaats?", en: "Where does the workshop take place?" },
        answer: {
          nl: "Zowel op locatie als volledig remote, afhankelijk van wat voor jouw team het beste werkt.",
          en: "Both on location and fully remote, depending on what works best for your team.",
        },
      },
      {
        question: { nl: "Verzorgen jullie ook een lezing of presentatie?", en: "Do you also give talks or presentations?" },
        answer: {
          nl: "Ja, naast workshops verzorgen we presentaties en executive briefings voor leidinggevenden en technische teams.",
          en: "Yes, alongside workshops we deliver presentations and executive briefings for leadership and technical teams.",
        },
      },
    ],
    relatedCaseIds: [],
    relatedKnowledgeIds: ["wanneer-geen-ai", "wat-kost-maatwerk-ai"],
  },

  {
    id: "ai-knowledge-systems",
    pageType: "service",
    slug: { nl: "ai-kennisbanken", en: "ai-knowledge-systems" },
    title: {
      nl: "AI Chatbot Laten Maken op Eigen Data | RAG Kennisbank",
      en: "Build an AI Knowledge Base | Custom RAG Chatbot",
    },
    description: {
      nl: "AI-chatbot laten maken op je eigen documenten en data? Wij bouwen RAG-chatbots en AI-kennisbanken die bedrijfsdocumentatie conversationeel toegankelijk maken, zoals voor Burgerkracht.",
      en: "Need a RAG chatbot or custom AI knowledge base? We build systems that make company documentation accessible through conversation, like we did for Burgerkracht.",
    },
    h1: {
      nl: "AI Chatbot Laten Maken op Eigen Data",
      en: "AI Knowledge Systems & RAG",
    },
    shortDescription: {
      nl: "We maken bedrijfswissen conversatie-gaandeweg vindbaar: systemen die documentatie begrijpen en exact relevante antwoorden ophalen.",
      en: "We make company knowledge findable through conversation: systems that understand documentation and retrieve exactly the relevant answers.",
    },
    intro: {
      heading: {
        nl: "Jouw documentatie wordt een converseerbare kennisbank",
        en: "Your documentation becomes a conversational knowledge base",
      },
      body: {
        nl: "Organisaties zitten vol kennis — handleidingen, beleid, veelgestelde vragen, projectdocumentatie — maar die kennis is alleen nuttig als mensen haar vinden. Met een Retrieval-Augmented Generation-architectuur (RAG) bouwen we systemen die natuurlijke vragen begrijpen en daaruit exact relevante passages uit jouw eigen documentatie ophalen en verwerken tot een antwoord. Voor Burgerkracht, een groot communityplatform voor Limburg, bouwden we zo'n systeem: bezoekers stelden natuurlijke vragen en werden automatisch doorverwezen naar de meest relevante groepen en informatie.",
        en: "Organisations are full of knowledge — manuals, policies, FAQs, project documentation — but knowledge is only useful when people can find it. Using a Retrieval-Augmented Generation architecture (RAG), we build systems that understand natural questions, retrieve exactly the relevant passages from your own documentation, and turn them into an answer. For Burgerkracht, a large community platform in Limburg, we built exactly such a system: visitors asked natural questions and were automatically referred to the most relevant groups and information.",
      },
    },
    capabilities: [
      {
        title: { nl: "Kennisbank-ontwikkeling", en: "Knowledge base development" },
        description: {
          nl: "We structureren en indexeren jouw documentatie zodanig dat retrieval snel en nauwkeurig is, ook bij grote hoeveelheden tekst.",
          en: "We structure and index your documentation so retrieval is fast and accurate, even across large volumes of text.",
        },
      },
      {
        title: { nl: "Conversationele interfaces", en: "Conversational interfaces" },
        description: {
          nl: "Gebruikers stellen vragen in gewone taal; het systeem begrijpt de vraag en haalt de relevante kennis op.",
          en: "Users ask questions in plain language; the system understands them and retrieves the relevant knowledge.",
        },
      },
      {
        title: { nl: "Dynamische retrieval en verwijzing", en: "Dynamic retrieval and referral" },
        description: {
          nl: "Antwoorden zijn gekoppeld aan de bron: gebruikers komen uit bij de juiste pagina, groep of het juiste document.",
          en: "Answers are linked to their source: users end up at the right page, group or document.",
        },
      },
      {
        title: { nl: "Privacy en grenzen", en: "Privacy and boundaries" },
        description: {
          nl: "We geven zorgvuldig aan welke data het systeem raadpleegt en wat het níet antwoordt, met aandacht voor privacygevoelige informatie.",
          en: "We carefully define which data the system consults and what it will not answer, with attention to privacy-sensitive information.",
        },
      },
    ] ,
    suitableFor: [
      {
        nl: "Medewerkers of klanten kunnen documentatie niet makkelijk vinden en stellen dezelfde vragen telkens opnieuw.",
        en: "Employees or customers cannot easily locate documentation and ask the same questions again and again.",
      },
      {
        nl: "De kennis staat verspreid over veel documenten, pagina's of groepen.",
        en: "Knowledge is spread across many documents, pages or groups.",
      },
      {
        nl: "Antwoorden moeten gestoeld zijn op jouw eigen informatie, niet op algemene modelkennis.",
        en: "Answers must be grounded in your own information, not general model knowledge.",
      },
    ] ,
    process: [
      {
        title: { nl: "Kennis inventariseren", en: "Inventory the knowledge" },
        description: {
          nl: "Welke bronnen zijn er, hoe actueel zijn ze, en welke vragen moeten eruit beantwoord kunnen worden?",
          en: "Which sources exist, how current are they, and which questions should they answer?",
        },
      },
      {
        title: { nl: "Retrievarchitectuur ontwerpen", en: "Design the retrieval architecture" },
        description: {
          nl: "We ontwerpen hoe vragen worden gematcht met kennis: indexatie, zoekstrategie en kwaliteitscontroles.",
          en: "We design how questions are matched with knowledge: indexing, search strategy and quality controls.",
        },
      },
      {
        title: { nl: "Interface bouwen", en: "Build the interface" },
        description: {
          nl: "Een conversationele laag waarin bezoekers of medewerkers natuurlijke vragen stellen en bruikbare verwijzingen krijgen.",
          en: "A conversational layer where visitors or staff ask natural questions and receive useful referrals.",
        },
      },
      {
        title: { nl: "Kennis actueel houden", en: "Keep knowledge current" },
        description: {
          nl: "We richten het beheer zo in dat nieuwe documentatie eenvoudig toegevoegd wordt en antwoorden actueel blijven.",
          en: "We set up management so new documentation is easily added and answers stay up to date.",
        },
      },
    ] ,
    faq: [
      {
        question: { nl: "Wat is een AI-chatbot op eigen data?", en: "What is an AI chatbot on your own data?" },
        answer: {
          nl: "Een chatbot die vragen beantwoordt op basis van jouw eigen documenten, handleidingen en kennis in plaats van op algemene modelkennis. Technisch heet dit Retrieval-Augmented Generation (RAG).",
          en: "A chatbot that answers questions based on your own documents, manuals and knowledge instead of general model knowledge. Technically this is called Retrieval-Augmented Generation (RAG).",
        },
      },
      {
        question: { nl: "Waarom is een RAG-chatbot nauwkeuriger dan een gewone chatbot?", en: "Why is a RAG chatbot more accurate than an ordinary chatbot?" },
        answer: {
          nl: "Omdat hij eerst de relevante passages uit jouw documentatie ophaalt en het antwoord daarop baseert. Antwoorden zijn gekoppeld aan hun bron, zodat gebruikers bij het juiste document of de juiste pagina uitkomen.",
          en: "Because it first retrieves the relevant passages from your documentation and bases the answer on them. Answers are linked to their source, so users end up at the right document or page.",
        },
      },
      {
        question: { nl: "Wat doen jullie met privacygevoelige informatie?", en: "How do you handle privacy-sensitive information?" },
        answer: {
          nl: "We leggen zorgvuldig vast welke data het systeem raadpleegt en wat het niet beantwoordt, met aandacht voor privacygevoelige informatie.",
          en: "We carefully define which data the system consults and what it will not answer, with attention to privacy-sensitive information.",
        },
      },
      {
        question: { nl: "Kunnen jullie de chatbot koppelen aan bestaande systemen?", en: "Can you connect the chatbot to existing systems?" },
        answer: {
          nl: "Ja. Een kennisbank-chatbot kan worden uitgebreid tot een AI-agent die ook tools en systemen aanstuurt. Zie onze pagina over AI-agents.",
          en: "Yes. A knowledge-base chatbot can be extended into an AI agent that also operates tools and systems. See our page on AI agents.",
        },
      },
    ],
    relatedCaseIds: ["burgerkracht"],
    relatedKnowledgeIds: ["rag-chatbot-laten-bouwen", "ai-kennisbank-voor-bedrijven", "rag-vs-fine-tuning"],
  },

  {
    id: "ai-consultancy",
    pageType: "service",
    slug: { nl: "ai-consultancy", en: "ai-consultancy" },
    title: {
      nl: "AI Consultancy & Quickscan voor Bedrijven | AI Advies",
      en: "AI Consultancy & Quickscan for Businesses | AI Advice",
    },
    description: {
      nl: "AI-advies zonder hype: een AI-quickscan van je processen, een eerlijke haalbaarheidscheck en een concreet stappenplan. Soms is het advies om (nog) niet met AI te starten.",
      en: "AI advice without the hype: an AI quickscan of your processes, an honest feasibility check and a concrete roadmap. Sometimes the advice is not to start with AI (yet).",
    },
    h1: {
      nl: "AI Consultancy & Quickscan",
      en: "AI Consultancy & Quickscan",
    },
    shortDescription: {
      nl: "Een nuchtere blik op waar AI in jouw organisatie waarde toevoegt, wat het vraagt en in welke volgorde je het slim aanpakt.",
      en: "A sober look at where AI adds value in your organisation, what it requires and in which order to approach it.",
    },
    intro: {
      heading: {
        nl: "Eerst weten waar AI loont, dan pas bouwen",
        en: "First know where AI pays off, then build",
      },
      body: {
        nl: "Veel AI-projecten stranden omdat ze bij de techniek beginnen in plaats van bij het proces. In een AI-quickscan brengen we samen met jou in kaart welke processen zich lenen voor AI, hoe het met de data is gesteld en wat een realistische eerste stap is. Omdat we zelf bouwen, beoordelen we haalbaarheid vanuit de praktijk en niet vanuit een slidedeck. En als blijkt dat AI (nog) niet de juiste oplossing is, zeggen we dat: eerlijkheid gaat voor omzet.",
        en: "Many AI projects stall because they start with the technology instead of the process. In an AI quickscan we map out together with you which processes suit AI, what state the data is in and what a realistic first step looks like. Because we build ourselves, we judge feasibility from practice rather than from a slide deck. And if it turns out AI is not (yet) the right solution, we say so: honesty before revenue.",
      },
    },
    capabilities: [
      {
        title: { nl: "Procesinventarisatie en kansenanalyse", en: "Process inventory and opportunity analysis" },
        description: {
          nl: "We identificeren welke taken repetitief, interpretatiegevoelig of kennisintensief zijn en dus kandidaat zijn voor AI.",
          en: "We identify which tasks are repetitive, interpretation-heavy or knowledge-intensive and therefore candidates for AI.",
        },
      },
      {
        title: { nl: "Haalbaarheids- en datacheck", en: "Feasibility and data check" },
        description: {
          nl: "We toetsen of de benodigde data beschikbaar, toegankelijk en van voldoende kwaliteit is en welke integraties nodig zijn.",
          en: "We test whether the required data is available, accessible and of sufficient quality, and which integrations are needed.",
        },
      },
      {
        title: { nl: "Prioritering en stappenplan", en: "Prioritisation and roadmap" },
        description: {
          nl: "Een concreet plan: welke use case eerst, welke aanpak past en hoe je succes meet.",
          en: "A concrete plan: which use case first, which approach fits and how to measure success.",
        },
      },
      {
        title: { nl: "Tool- en bouw-of-koop-advies", en: "Tool and build-or-buy advice" },
        description: {
          nl: "Wanneer volstaat een bestaand product en wanneer loont maatwerk? We helpen je die afweging te maken.",
          en: "When does an existing product suffice and when does custom work pay off? We help you make that call.",
        },
      },
      {
        title: { nl: "Eerlijk advies, ook als dat 'niet doen' is", en: "Honest advice, even when that means 'don't'" },
        description: {
          nl: "Soms is AI (nog) niet de juiste oplossing. Dan zeggen we dat, en schetsen we wat er eerst op orde moet zijn.",
          en: "Sometimes AI is not (yet) the right solution. Then we say so, and outline what has to be in order first.",
        },
      },
    ],
    suitableFor: [
      {
        nl: "Je weet dat AI kansen biedt, maar niet welk proces je het beste als eerste aanpakt.",
        en: "You know AI offers opportunities, but not which process to tackle first.",
      },
      {
        nl: "Je hebt een idee voor AI en wilt eerst weten of het haalbaar en de investering waard is.",
        en: "You have an idea for AI and first want to know whether it is feasible and worth the investment.",
      },
      {
        nl: "Je wilt een onafhankelijke, technisch onderbouwde blik voordat je in een traject stapt.",
        en: "You want an independent, technically grounded view before committing to a project.",
      },
    ],
    process: [
      {
        title: { nl: "Kennismaking", en: "Introduction" },
        description: {
          nl: "We bespreken je organisatie, je doelen en waar het knelt.",
          en: "We discuss your organisation, your goals and where the friction is.",
        },
      },
      {
        title: { nl: "Processen en data in kaart", en: "Map processes and data" },
        description: {
          nl: "Samen inventariseren we welke processen en bronnen relevant zijn.",
          en: "Together we inventory which processes and sources are relevant.",
        },
      },
      {
        title: { nl: "Analyse en prioritering", en: "Analysis and prioritisation" },
        description: {
          nl: "We beoordelen haalbaarheid en waarde per kans en bepalen een logische volgorde.",
          en: "We assess feasibility and value per opportunity and determine a logical order.",
        },
      },
      {
        title: { nl: "Advies en vervolgstappen", en: "Advice and next steps" },
        description: {
          nl: "Je ontvangt een concreet advies, bijvoorbeeld een proof of concept, een workshop of de keuze om (nog) niet te starten.",
          en: "You receive concrete advice, for example a proof of concept, a workshop or the choice not to start (yet).",
        },
      },
    ],
    faq: [
      {
        question: { nl: "Wat is een AI-quickscan?", en: "What is an AI quickscan?" },
        answer: {
          nl: "Een compacte verkenning van je processen en data om te bepalen waar AI waarde toevoegt, wat haalbaar is en welke stap logisch als eerste volgt.",
          en: "A compact exploration of your processes and data to determine where AI adds value, what is feasible and which step logically comes first.",
        },
      },
      {
        question: { nl: "Wat levert AI-consultancy op?", en: "What does AI consultancy deliver?" },
        answer: {
          nl: "Inzicht in kansen en risico's, een geprioriteerd stappenplan en een eerlijke inschatting van haalbaarheid. Daarmee neem je AI-beslissingen op basis van feiten in plaats van hype.",
          en: "Insight into opportunities and risks, a prioritised roadmap and an honest feasibility assessment, so you make AI decisions based on facts instead of hype.",
        },
      },
      {
        question: { nl: "Wat kost een AI-quickscan?", en: "What does an AI quickscan cost?" },
        answer: {
          nl: "Dat hangt af van de omvang van je organisatie en het aantal processen dat we bekijken. Na een kennismaking maken we een voorstel.",
          en: "That depends on the size of your organisation and the number of processes we look at. After an introduction we make a proposal.",
        },
      },
      {
        question: { nl: "Adviseren jullie ook om niet met AI te beginnen?", en: "Will you also advise against starting with AI?" },
        answer: {
          nl: "Ja, als AI (nog) niet de juiste oplossing is, zeggen we dat. Lees hierover ons artikel over wanneer je beter geen AI inzet.",
          en: "Yes. If AI is not (yet) the right solution, we say so. Read our article on when not to use AI.",
        },
      },
    ],
    relatedCaseIds: ["de-limburger"],
    relatedKnowledgeIds: ["wanneer-geen-ai", "van-ai-poc-naar-productie", "wat-kost-maatwerk-ai"],
  },

  {
    id: "ai-lectures",
    pageType: "service",
    slug: { nl: "ai-lezingen", en: "ai-keynotes" },
    title: {
      nl: "AI Lezing voor Bedrijven | AI Spreker Inhuren",
      en: "AI Keynotes & Talks for Organisations | Hire an AI Speaker",
    },
    description: {
      nl: "Een AI-lezing of keynote voor je bedrijf, team of event? Heldere presentaties over wat AI wel en niet kan, door ingenieurs die AI-systemen zelf bouwen.",
      en: "An AI talk or keynote for your company, team or event? Clear presentations on what AI can and cannot do, by engineers who build AI systems themselves.",
    },
    h1: {
      nl: "AI Lezing & Keynote voor Bedrijven",
      en: "AI Keynotes & Talks",
    },
    shortDescription: {
      nl: "Presentaties en executive briefings over AI die verder gaan dan hype: wat kan het, waar zitten de risico's en wat betekent het voor jouw organisatie.",
      en: "Presentations and executive briefings on AI that go beyond hype: what it can do, where the risks are and what it means for your organisation.",
    },
    intro: {
      heading: {
        nl: "AI uitgelegd door mensen die het zelf bouwen",
        en: "AI explained by people who build it themselves",
      },
      body: {
        nl: "Een goede AI-lezing geeft je publiek houvast: wat kan AI vandaag echt, waar zitten de risico's en wat betekent dat voor het werk van jouw team? Wij vertalen complexe AI-concepten naar heldere praktijk, met echte voorbeelden uit projecten die we zelf bouwden. Van een briefing voor de directie tot een lezing voor een team of event.",
        en: "A good AI talk gives your audience something to hold on to: what can AI really do today, where are the risks and what does that mean for your team's work? We translate complex AI concepts into clear practice, with real examples from projects we built ourselves. From a briefing for leadership to a talk for a team or event.",
      },
    },
    capabilities: [
      {
        title: { nl: "Executive briefings", en: "Executive briefings" },
        description: {
          nl: "Compacte sessies voor leidinggevenden over AI-kansen, trends en wat het vraagt van de organisatie.",
          en: "Compact sessions for leaders on AI opportunities, trends and what it demands of the organisation.",
        },
      },
      {
        title: { nl: "Lezing voor team of event", en: "Talks for teams or events" },
        description: {
          nl: "Een heldere presentatie voor een groter publiek, afgestemd op hun voorkennis en werkveld.",
          en: "A clear presentation for a larger audience, tailored to their prior knowledge and field.",
        },
      },
      {
        title: { nl: "Technische deep-dives", en: "Technical deep-dives" },
        description: {
          nl: "Voor developmentteams: architectuur, afwegingen en lessen uit het bouwen van AI-systemen in de praktijk.",
          en: "For development teams: architecture, trade-offs and lessons from building AI systems in practice.",
        },
      },
      {
        title: { nl: "Ruimte voor vragen en discussie", en: "Room for questions and discussion" },
        description: {
          nl: "Geen eenrichtingsverkeer: vragen en demo's horen erbij.",
          en: "Never one-directional: questions and demos are part of it.",
        },
      },
    ],
    suitableFor: [
      {
        nl: "Je wilt je team of publiek op een begrijpelijke manier informeren over AI.",
        en: "You want to inform your team or audience about AI in an understandable way.",
      },
      {
        nl: "Je organiseert een event, kennissessie of teamdag en zoekt een AI-spreker.",
        en: "You are organising an event, knowledge session or team day and are looking for an AI speaker.",
      },
      {
        nl: "Leidinggevenden willen AI-beslissingen nemen op basis van inzicht in plaats van marketing.",
        en: "Leaders want to make AI decisions based on insight rather than marketing.",
      },
    ],
    process: [
      {
        title: { nl: "Doel en publiek bepalen", en: "Define goal and audience" },
        description: {
          nl: "Wie zit er in de zaal, wat weten ze al en wat moeten ze na afloop begrijpen?",
          en: "Who is in the room, what do they already know and what should they understand afterwards?",
        },
      },
      {
        title: { nl: "Inhoud op maat", en: "Tailored content" },
        description: {
          nl: "We stemmen voorbeelden en diepgang af op jouw sector en publiek.",
          en: "We tune examples and depth to your industry and audience.",
        },
      },
      {
        title: { nl: "Presentatie met ruimte voor vragen", en: "Presentation with room for questions" },
        description: {
          nl: "De lezing zelf, met ruimte voor interactie en discussie.",
          en: "The talk itself, with room for interaction and discussion.",
        },
      },
      {
        title: { nl: "Vervolg", en: "Follow-up" },
        description: {
          nl: "Op wens koppelen we een workshop of vervolgtraject aan de lezing.",
          en: "If you wish, we link a workshop or follow-up programme to the talk.",
        },
      },
    ],
    faq: [
      {
        question: { nl: "Voor welke gelegenheden is een AI-lezing geschikt?", en: "For which occasions is an AI talk suitable?" },
        answer: {
          nl: "Voor teamsessies, directie-overleg, kennisdagen en events: overal waar een publiek helder wil begrijpen wat AI voor hen betekent.",
          en: "For team sessions, leadership meetings, knowledge days and events: anywhere an audience wants to understand clearly what AI means for them.",
        },
      },
      {
        question: { nl: "Is de lezing op maat?", en: "Is the talk tailored?" },
        answer: {
          nl: "Ja. We stemmen inhoud en diepgang af op je publiek en sector.",
          en: "Yes. We tune content and depth to your audience and industry.",
        },
      },
      {
        question: { nl: "Kan een lezing worden gecombineerd met een workshop?", en: "Can a talk be combined with a workshop?" },
        answer: {
          nl: "Ja. Een lezing geeft overzicht; een workshop laat je team er zelf mee oefenen. Zie onze AI-workshops.",
          en: "Yes. A talk gives an overview; a workshop lets your team practise with it. See our AI workshops.",
        },
      },
    ],
    relatedCaseIds: [],
    relatedKnowledgeIds: ["wanneer-geen-ai", "ai-agents-vs-automatisering"],
  },
]

export function getService(id: string): ServicePage | undefined {
  return services.find((s) => s.id === id)
}

export function getServiceBySlug(slug: string, locale: Locale): ServicePage | undefined {
  return services.find((s) => s.slug[locale] === slug)
}
